import { memo, useEffect, useState } from "react";
import type { GlobalNotificationT } from "../../../../features/NewPrices2024/models/models";
import dayjs from "dayjs";
import Icon from "../../../Icon/Icon";
import { IconColorE } from "../../../../models/enums";
import confetiImage from "../../../../assets/images/confeti.png";
import styles from "../../styles.module.scss";
import CampaignButton from "../../../CampaignButton";

interface GlobalNotificationCampaignContentI {
  notification: GlobalNotificationT;
  onClose?: () => void;
  onNavigate?: () => void;
  isPreview?: boolean;
  isMobileStrip?: boolean;
}

/**
 * Shared component for Campaign notification UI.
 * Contains countdown timer, gift button, and confetti animation.
 */
const GlobalNotificationCampaignContent = memo(
  (props: GlobalNotificationCampaignContentI) => {
    const { notification, onClose, isMobileStrip } = props;
    const [timeLeft, setTimeLeft] = useState({
      days: 0,
      hours: 0,
      minutes: 0,
    });

    // Note: Campaign notifications are NOT marked as read in localStorage
    // They should be shown until user manually closes them

    // Calculate countdown timer
    useEffect(() => {
      const calculateTimeLeft = () => {
        const endDate = dayjs(notification.ToDate);
        const now = dayjs();
        const diff = endDate.diff(now);

        if (diff <= 0) {
          // Don't auto-close, just show zeros
          setTimeLeft({ days: 0, hours: 0, minutes: 0 });
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        setTimeLeft({ days, hours, minutes });
      };

      calculateTimeLeft();
      const interval = setInterval(calculateTimeLeft, 60000); // Update every minute

      return () => clearInterval(interval);
    }, [notification.ToDate]);

    const containerClassName = isMobileStrip
      ? `${styles.globalNotificationCampaignBanner} ${styles.mobileStrip}`
      : styles.globalNotificationCampaignBanner;

    return (
      <div className={containerClassName}>
        {onClose && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" color={IconColorE.WHITE} />
          </button>
        )}

        <ul className={styles.countdownWrapper}>
          <li className={styles.countdownItem}>
            <div className={styles.countdownNumbers}>
              {String(timeLeft.days)
                .padStart(2, "0")
                .split("")
                .map((digit, index) => (
                  <div key={index} className={styles.countdownNumber}>
                    {digit}
                  </div>
                ))}
            </div>
            <div className={styles.countdownLabel}>D</div>
          </li>
          <li className={styles.countdownItem}>
            <div className={styles.countdownNumbers}>
              {String(timeLeft.hours)
                .padStart(2, "0")
                .split("")
                .map((digit, index) => (
                  <div key={index} className={styles.countdownNumber}>
                    {digit}
                  </div>
                ))}
            </div>
            <div className={styles.countdownLabel}>H</div>
          </li>
          <li className={styles.countdownItem}>
            <div className={styles.countdownNumbers}>
              {String(timeLeft.minutes)
                .padStart(2, "0")
                .split("")
                .map((digit, index) => (
                  <div key={index} className={styles.countdownNumber}>
                    {digit}
                  </div>
                ))}
            </div>
            <div className={styles.countdownLabel}>M</div>
          </li>
        </ul>

        <CampaignButton id="sidebar" />

        <div className={styles.confettiContainer}>
          <img
            src={confetiImage}
            alt="confetti"
            className={styles.confettiImage}
          />
        </div>
      </div>
    );
  }
);

export default GlobalNotificationCampaignContent;
